import React from "react";
// ============================================================================
// CreativeEnhancementsSection Component (Template Editor Wrapper)
// ============================================================================
// Purpose: Thin wrapper around the shared EnhancementTogglesPanel for use
//          in the Ad Template editor.
// ============================================================================

import { EnhancementTogglesPanel } from "@/components/shared/EnhancementTogglesPanel";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Sparkles } from "lucide-react";

// Re-export conversion utilities from shared module for backward compatibility
export {
  templateToToggleValues,
  toggleValuesToTemplateColumns,
  buildToggleToColumnMapping,
} from "@/utils/enhancementMappingUtils";
export type {
  ToggleToColumnMapping,
  ColumnMapping,
} from "@/utils/enhancementMappingUtils";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CreativeEnhancementsSectionProps {
  /** Flat toggle values keyed by DB toggle_key */
  values: Record<string, boolean>;
  /** Called when any toggle changes */
  onChange: (key: string, value: boolean) => void;
  /** Called for batch updates (e.g. Enable All) */
  onBatchChange?: (updates: Record<string, boolean>) => void;
  /** Collapsible state */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreativeEnhancementsSection({
  values,
  onChange,
  onBatchChange,
  isOpen,
  onOpenChange,
}: CreativeEnhancementsSectionProps) {
  return (
    <div className="bg-muted/50 rounded-lg border">
      <Collapsible open={isOpen} onOpenChange={onOpenChange}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/70 transition-colors rounded-t-lg">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-primary" />
              <div>
                <h3 className="font-semibold">Creative Enhancements (Advantage+)</h3>
                <p className="text-sm text-muted-foreground">
                  Control which Meta AI enhancements are applied to your ads
                </p>
              </div>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-4 pt-0">
            <EnhancementTogglesPanel
              values={values}
              onChange={onChange}
              onBatchChange={onBatchChange}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
